import React, { useState, useEffect } from "react";

import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";

import useClickOutside from "../../utils/CustomHooks/useClickOutside";

const BaseAlertModal = ({
    isVisible,
    setIsVisible,
    iconClassName,
    title,
    message,
    action,
    type,
    children,
    isSubmitting,
    secondActionButton,
    includeCancel = true,
    includeSubmit = true,
}) => {
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const { t } = useTranslation();
    const modalRef = useClickOutside(() => {
        setIsVisible(false);
    });

    const MODAL_TYPES = {
        confirmation: {
            icon: "fas fa-check-square",
            actionName: t("confirm"),
        },
        delete: {
            icon: "fas fa-trash-alt",
            actionName: t("delete"),
        },
        warning: {
            icon: "fas fa-exclamation-triangle",
        },
    };

    useEffect(() => {
        if (!(isSubmitting === null)) setModalSubmitting(isSubmitting);
    }, [isSubmitting]);

    const closeModal = () => {
        setIsVisible(false);
        setModalSubmitting(false);
    };

    const handleCancelClicked = () => {
        action?.cancelCallback
            ? action.cancelCallback().then(() => {
                  closeModal();
              })
            : closeModal();
    };

    const handleActionSubmitted = () => {
        setModalSubmitting(true);
        action
            ?.submitAction()
            ?.then(() => {
                closeModal();
            })
            .catch((error) => {
                error && console.log(error);
            })
            .finally(() => {
                setModalSubmitting(false);
            });
    };

    const renderButton = () => {
        if (modalSubmitting) {
            return (
                <>
                    <span className="animate-spin mr-3 inline-flex">
                        <i className="fas fa-sync"></i>
                    </span>
                    <Trans i18nKey="requesting">Requesting...</Trans>
                </>
            );
        } else {
            return (
                action?.name ||
                MODAL_TYPES[type]?.actionName || (
                    <Trans i18nKey="submit">Submit</Trans>
                )
            );
        }
    };

    const renderSubmit = () => {
        if (!includeSubmit) return;
        return (
            <button
                onClick={handleActionSubmitted}
                disabled={modalSubmitting || action?.disabled}
                className={`${
                    action?.outline
                        ? "default-orange-outline-btn"
                        : "default-orange-btn"
                } ${modalSubmitting || action?.disabled ? "disabled" : ""}`}
            >
                {renderButton()}
            </button>
        );
    };

    const renderFooter = () => {
        if (!includeCancel && !includeSubmit) return;
        return (
            <div className="modal-footer">
                <div className="modal-btn-actions-area flex items-center justify-center">
                    {includeCancel && (
                        <button
                            onClick={handleCancelClicked}
                            className="default-transparent-btn modal-btn-close mr-2"
                            data-modal-close="delete-message-modal"
                        >
                            <Trans i18nKey="cancel">Cancel</Trans>
                        </button>
                    )}
                    {renderSubmit()}
                    {secondActionButton}
                </div>
            </div>
        );
    };

    return (
        <>
            <div
                className={`modal small-scroll-y ${isVisible ? "show" : ""}`}
                id="delete-message-modal"
            >
                <div
                    ref={modalRef}
                    className="modal-content-wrapper md-modal shadow"
                >
                    <div className="flex">
                        <div
                            onClick={handleCancelClicked}
                            className="icon-wrapper ml-auto mt-2 mr-4"
                        >
                            <i className="fas fa-times" />
                        </div>
                    </div>
                    <div className="modal-content">
                        <div className="message-modal-wrapper delete-box flex flex-col items-center justify-center">
                            {(iconClassName || MODAL_TYPES[type]?.icon) && (
                                <i
                                    className={`${
                                        iconClassName || MODAL_TYPES[type]?.icon
                                    } main-icon pt-2`}
                                ></i>
                            )}
                            <h2 className="text-center">{title}</h2>
                            <p className="text-center">{message}</p>
                            {children}
                        </div>
                    </div>
                    {renderFooter()}
                </div>
            </div>
        </>
    );
};

BaseAlertModal.Proptypes = {
    children: PropTypes.node,
    isVisible: PropTypes.bool.isRequired,
    setIsVisible: PropTypes.func.isRequired,
    iconClassName: PropTypes.string,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    action: PropTypes.shape({
        name: PropTypes.string,
        disabled: PropTypes.bool,
        submitAction: PropTypes.func.isRequired,
        outline: PropTypes.bool,
        cancelCallback: PropTypes.func,
    }),
    isSubmitting: PropTypes.bool,
    secondActionButton: PropTypes.node,
    type: PropTypes.oneOf(["confirmation", "delete", "warning"]),
};

export default BaseAlertModal;
